// src/components/TableMovimientos.jsx
import React, { useState, useEffect, useRef } from "react";
import { Table, Spinner } from "react-bootstrap";
import {
  getMovimientos,
  deleteMovimiento,
  getMovimientoById,
} from "../helpers/rutaMovimientos";
import ModalMovimiento from "./ModalMovimiento";
import "../css/tablecomandas.css";

/* ========== CONFIG DE ANCHOS (tuneable) ========== */
const COLS = {
  acciones: 90,
  nro: 90,
  fecha: 120,
  fechaValor: 120,
  tipo: 110,
  medio: 140,
  monto: 140,
  descripcion: 260,
  destino: 260,
  gasto: 220,
  persona: 220,
  viaje: 120,
  camion: 160,
  chofer: 180,
  empresa: 220,
  cliente: 220,
  libre: 220,
  origen: 130,
  estado: 120,
  usuario: 160,
};
/* ================================================ */

// helpers
const S = (v) => (typeof v === "string" ? v : v == null ? "" : String(v));
const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

const fmtFecha = (d) =>
  d
    ? new Date(d).toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";

const fmtMoneda = (n) =>
  typeof n === "number"
    ? n.toLocaleString("es-AR", {
        style: "currency",
        currency: "ARS",
        minimumFractionDigits: 2,
      })
    : "-";

const w = (px) => ({ width: px, minWidth: px, maxWidth: px });
const clamp = (px, align = "left") => ({
  ...w(px),
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  textAlign: align,
});

const TableMovimientos = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // para editar
  const [movimiento, setMovimiento] = useState(null);
  const [show, setShow] = useState(false);

  // evitar doble fetch en StrictMode
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    consultaMovimientos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseLista = (datos) => {
    if (!datos) return [];
    if (Array.isArray(datos.movimientos)) return datos.movimientos;
    if (Array.isArray(datos.items)) return datos.items;
    if (Array.isArray(datos)) return datos;
    return [];
  };

  const isHydrated = (list) => {
    const m = list?.[0];
    if (!m) return false;
    return [
      m.fondo || m.caja || m.cuentaBancaria,
      m.gasto,
      m.persona,
      m.camion,
      m.chofer,
      m.empresa,
      m.cliente,
    ].some((x) => x && typeof x === "object");
  };

  const consultaMovimientos = async () => {
    try {
      setLoading(true);
      const datos = await getMovimientos({ full: 1 });
      const list = parseLista(datos) || [];
      setRows(list);

      if (isHydrated(list)) {
        setLoading(false);
        return;
      }

      await enrichFirstPage(list);
      setLoading(false);
    } catch (e) {
      console.error("[MOVIMIENTOS] GET error:", e);
      setRows([]);
      setLoading(false);
    }
  };

  // enriquecimiento rápido (primeras filas visibles)
  const VISIBLE_TO_ENRICH = 60;
  const CONCURRENCY = 16;
  const KEYS = ["fondo", "caja", "cuentaBancaria", "gasto", "persona", "camion", "chofer", "empresa", "cliente"];
  const needsEnrich = (m) => KEYS.some((k) => m[k] && typeof m[k] === "string");

  const enrichFirstPage = async (list) => {
    const target = list.slice(0, VISIBLE_TO_ENRICH).filter(needsEnrich);
    if (target.length === 0) return;

    let idx = 0;
    const updated = [...list];

    const worker = async () => {
      while (idx < target.length) {
        const current = target[idx++];
        try {
          const resp = await getMovimientoById(current._id);
          if (resp?.ok && resp.movimiento) {
            const det = resp.movimiento;
            const i = updated.findIndex((x) => x._id === current._id);
            if (i >= 0) {
              const merged = { ...updated[i] };
              KEYS.forEach((k) => {
                if (det[k]) merged[k] = det[k];
              });
              updated[i] = merged;
            }
          }
        } catch {
          /* noop */
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, target.length) }, () => worker())
    );
    setRows(updated);
  };

  // editar
  const handleClose = () => {
    setShow(false);
    consultaMovimientos();
  };
  const handleShow = () => setShow(true);

  const modificaMovimiento = async (id) => {
    try {
      const resp = await getMovimientoById(id);
      if (resp?.ok) {
        setMovimiento(resp.movimiento);
        handleShow();
      } else {
        alert(resp?.error || "No se pudo leer el movimiento");
      }
    } catch {
      alert("No se pudo leer el movimiento");
    }
  };

  // eliminar
  const eliminarMovimiento = async (id) => {
    if (!window.confirm("¿Está seguro que desea borrar el movimiento?")) return;
    try {
      const resp = await deleteMovimiento(id);
      if (resp?.ok) consultaMovimientos();
      else alert(resp?.error || "No se pudo borrar el movimiento");
    } catch {
      alert("No se pudo borrar el movimiento");
    }
  };

  // celdas
  const renderDestino = (mov) => {
    const medio = S(mov.medio).toUpperCase();
    const fondo = mov.fondo || mov.caja || mov.cuentaBancaria; // tolera nombres antiguos
    if (medio === "EFECTIVO") {
      return isObj(fondo)
        ? fondo.descripcion || fondo.nombre || fondo.codcaj || "EFECTIVO"
        : "EFECTIVO (—)";
    }
    if (medio === "TRANSFERENCIA") {
      const c = isObj(fondo) ? fondo : null;
      return c ? c.alias || [c.banco, c.numero].filter(Boolean).join(" ") : "—";
    }
    if (medio === "CHEQUE" || medio === "ICHEQ") return "—";
    return "—";
  };

  const renderGasto = (mov) => {
    if (isObj(mov.gasto)) {
      const tipo = mov.gasto.tipo || mov.gasto.categoria || mov.gasto.rubro || null;
      const nombre = S(mov.gasto.gasto).trim();
      return (
        <>
          <div style={{ fontWeight: 600 }}>{tipo || (nombre || "—")}</div>
          {tipo && nombre && <small className="text-muted">{nombre}</small>}
        </>
      );
    }
    return "—";
  };




  const renderViaje = (mov) => {
    if (isObj(mov.comanda)) {
      const nv = mov.comanda.nroviaje || mov.comanda.codcom;
      return nv ? ` ${nv}` : "—";
    }
    return "—";
  };

  const renderCamion = (mov) =>
    isObj(mov.camion) ? mov.camion.camion || mov.camion.patente || "—" : "—";

  const renderChofer = (mov) =>
    isObj(mov.chofer) ? mov.chofer.nombre || "—" : "—";

  const renderPersona = (mov) =>
    isObj(mov.persona)
      ? mov.persona.nombre || mov.persona.razonSocial || `#${mov.persona.codper || ""}` || "—"
      : "—";

  const renderEmpresa = (mov) =>
    isObj(mov.empresa)
      ? mov.empresa.empresa || mov.empresa.razonSocial || `#${mov.empresa.codemp || ""}` || "—"
      : "—";

  const renderCliente = (mov) =>
    isObj(mov.cliente)
      ? (mov.cliente.razonsocial ||
        mov.cliente.nombreFantasia ||
        `#${mov.cliente.codcli || ""}`) || "—"
      : "—";


  const renderOrigen = (mov) => {
    const t = S(mov.origenTipo).toUpperCase();
    if (!t) return "—";
    const id = mov.origenId ? String(mov.origenId).slice(-6) : "";
    return id ? `${t} · …${id}` : t;
  };

  const renderUsuario = (mov) => {
    if (isObj(mov.usuario)) {
      return mov.usuario.nombre || mov.usuario.email || "—";
    }
    const s = S(mov.usuario);
    return s ? `…${s.slice(-6)}` : "—";
  };

  return (
    <div className="mov-table table-container">
      {loading ? (
        <div className="d-flex align-items-center gap-2 p-3">
          <Spinner animation="border" size="sm" />
          <span>Cargando movimientos…</span>
        </div>
      ) : (
        <>
          <div className="col-12 mt-3" />
          <div>
            <Table striped bordered hover className="table-container">
              <thead>
                <tr className="header">
                  <th className="acciones" style={w(COLS.acciones)}>Acciones</th>
                  <th style={w(COLS.nro)}>Nro</th>
                  <th style={w(COLS.fecha)}>Fecha</th>
                  <th style={w(COLS.fechaValor)}>F. Valor</th>
                  <th style={w(COLS.tipo)}>Tipo</th>
                  <th style={w(COLS.medio)}>Medio</th>
                  <th style={clamp(COLS.monto, "center")}>Monto</th>
                  <th style={clamp(COLS.descripcion, "center")}>Descripción</th>
                  <th style={clamp(COLS.destino, "center")}>Fondo / Destino</th>
                  <th style={clamp(COLS.gasto, "center")}>Gasto</th>
                  {/* <th style={w(COLS.viaje)}>Viaje</th> */}
                  <th style={w(COLS.camion)}>Vehiculo</th>
                  <th style={w(COLS.chofer)}>Chofer</th>
                  <th style={w(COLS.persona)}>Persona</th>
                  <th style={w(COLS.empresa)}>Empresa</th>
                  <th style={w(COLS.cliente)}>Cliente</th>
                  <th style={clamp(COLS.libre)}>Libre</th>
                  <th style={w(COLS.origen)}>Origen</th>
                  <th style={w(COLS.estado)}>Estado</th>
                  <th style={w(COLS.usuario)}>Usuario</th>
                </tr>
              </thead>

              <tbody>
                {(rows || []).map((mov, idx) =>
                  mov?.activo !== false ? (
                    <tr key={mov._id || idx}>
                      <td className="acciones" style={w(COLS.acciones)}>
                        <button
                          className="btn btn-primary"
                          onClick={() => modificaMovimiento(mov._id)}
                          title="Editar"
                        >
                          <i className="fa fa-pencil-square-o" aria-hidden="true" />
                        </button>
                        <button
                          className="btn btn-danger ml-2"
                          onClick={() => eliminarMovimiento(mov._id)}
                          title="Eliminar"
                        >
                          <i className="fa fa-trash-o" aria-hidden="true" />
                        </button>
                      </td>

                      <td style={clamp(COLS.nro)}>{mov.codmov ?? "-"}</td>
                      <td style={clamp(COLS.fecha)}>{fmtFecha(mov.fecha)}</td>
                      <td style={clamp(COLS.fechaValor)}>{fmtFecha(mov.fechaValor)}</td>
                      <td style={{ ...w(COLS.tipo), textTransform: "capitalize" }}>{mov.tipo || "-"}</td>
                      <td style={w(COLS.medio)}>{mov.medio || "-"}</td>
                      <td style={clamp(COLS.monto, "right")}>{fmtMoneda(Number(mov.monto || 0))}</td>
                      <td style={clamp(COLS.descripcion)}>{S(mov.descripcion) || "-"}</td>
                      <td style={clamp(COLS.destino)}>{renderDestino(mov)}</td>
                      <td style={clamp(COLS.gasto)}>{renderGasto(mov)}</td>
                      {/* <td style={w(COLS.viaje)}>{renderViaje(mov)}</td> */}
                      <td style={w(COLS.camion)}>{renderCamion(mov)}</td>
                      <td style={w(COLS.chofer)}>{renderChofer(mov)}</td>
                      <td style={w(COLS.persona)}>{renderPersona(mov)}</td>
                      <td style={w(COLS.empresa)}>{renderEmpresa(mov)}</td>
                      <td style={w(COLS.cliente)}>{renderCliente(mov)}</td>
                      <td style={clamp(COLS.libre)}>{mov.libre || "-"}</td>
                      <td style={w(COLS.origen)}>{renderOrigen(mov)}</td>
                      <td style={w(COLS.estado)}>{S(mov.estado) || "activo"}</td>
                      <td style={w(COLS.usuario)}>{renderUsuario(mov)}</td>
                    </tr>
                  ) : null
                )}

                {(!rows || rows.length === 0) && (
                  <tr>
                    <td colSpan={19} style={{ textAlign: "center", color: "#777" }}>
                      Sin movimientos para mostrar
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Modal de edición */}
          <ModalMovimiento show={show} handleClose={handleClose} movimiento={movimiento} />
        </>
      )}
    </div>
  );
};

export default TableMovimientos;
